import { RotateCcw } from "lucide-react";
import {
  BaseAppointmentAction,
  AppointmentActionCategory,
  UserRole,
  AppointmentActionContext,
  AppointmentActionPayload,
  AppointmentActionResult,
} from "../core/base-action";
import { appointment_status } from "@/models/appointment.model";
import { cancelAppointment } from "@/services/appointment-cancellation.service";
import { sendBack } from "@/services/appointment-go-back.service";
import { chatStatusChoices } from "@/lib/appointment-go-back";
import { fieldActionMessage } from "@/lib/appointment-field-actions";

export class StatusChangeAction extends BaseAppointmentAction {
  readonly config = {
    id: "status_change",
    label: "Change Status",
    description: "Update appointment status (PMC only)",
    icon: RotateCcw,
    category: AppointmentActionCategory.STATUS,
    permissions: {
      requiredRole: [UserRole.PMC],
      excludedStatuses: [],
    },
    requiresDialog: true,
    variant: "default" as const,
  };

  validatePayload(payload?: AppointmentActionPayload): string | null {
    if (!payload?.nextStatus) {
      return "New status is required";
    }
    if (payload.nextStatus === appointment_status.goback && !payload.newDate) {
      return "A go back date is required";
    }
    return null;
  }

  getDialogComponent(): string {
    return "StatusChangeDialog";
  }

  isAvailable(context: AppointmentActionContext): boolean {
    if (!super.isAvailable(context)) {
      return false;
    }

    return chatStatusChoices(context.currentStatus).length > 0;
  }

  async execute(
    context: AppointmentActionContext,
    payload?: AppointmentActionPayload
  ): Promise<AppointmentActionResult> {
    try {
      const { nextStatus } = payload || {};

      if (!nextStatus) {
        throw new Error("New status is required");
      }

      if (!chatStatusChoices(context.currentStatus).includes(nextStatus)) {
        throw new Error(`Cannot transition from ${context.currentStatus} to ${nextStatus}`);
      }

      if (nextStatus === appointment_status.cancelled) {
        await cancelAppointment(context.appointmentId);
      } else {
        await sendBack(context.appointmentId, payload.newDate);
      }

      return {
        success: true,
        message: `Appointment status changed to ${nextStatus}`,
      };
    } catch (error) {
      return {
        success: false,
        message: fieldActionMessage(error, error instanceof Error ? error.message : "Failed to change appointment status"),
      };
    }
  }
}
